import { useState, useEffect, useCallback } from 'react';
import { geolocationService, GeolocationResult } from '@/services/geolocationService';

interface UseLocationTrackingOptions {
  respectUserPreference?: boolean;
  fallbackToIP?: boolean;
}

export const useLocationTracking = (options: UseLocationTrackingOptions = {}) => {
  const { respectUserPreference = true, fallbackToIP = true } = options;
  
  const [isLocationEnabled, setIsLocationEnabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load user's location tracking preference
  useEffect(() => {
    if (respectUserPreference) {
      const savedPreference = localStorage.getItem('location-tracking-enabled');
      if (savedPreference !== null) {
        setIsLocationEnabled(savedPreference === 'true');
      }
    }
  }, [respectUserPreference]);

  // Get location with proper privacy controls
  const getLocation = useCallback(async (requestPermission: boolean = false): Promise<GeolocationResult> => {
    setIsLoading(true);
    setError(null);

    try {
      // Check if location tracking is disabled by user
      if (respectUserPreference && !isLocationEnabled) {
        return {
          success: false,
          location: 'Location tracking disabled',
          error: 'User has disabled location tracking'
        };
      }

      // Check if user has already granted permission
      const hasPermission = await geolocationService.hasLocationPermission();
      
      if (hasPermission || requestPermission) {
        // Use actual geolocation with proper reverse geocoding
        const result = await geolocationService.getCurrentLocation({
          enableHighAccuracy: false,
          timeout: 8000,
          maximumAge: 300000, // 5 minutes cache
          fallbackToIP,
          useCache: true
        });
        
        return result;
      } else {
        // Use IP-based location without requesting permission
        const result = await geolocationService.getLocationWithoutPermission();
        return result;
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Location detection failed';
      setError(errorMessage);
      
      return {
        success: false,
        location: 'Unknown location',
        error: errorMessage
      };
    } finally {
      setIsLoading(false);
    }
  }, [isLocationEnabled, respectUserPreference, fallbackToIP]);

  // Get location for login activity (respects user preference)
  const getLocationForLoginActivity = useCallback(async (): Promise<string> => {
    if (respectUserPreference && !isLocationEnabled) {
      return 'Location tracking disabled';
    }

    try {
      const result = await getLocation(false); // Don't request permission for login activity
      return result.location;
    } catch (error) {
      console.warn('Login activity location detection failed:', error);
      return 'Unknown location';
    }
  }, [getLocation, isLocationEnabled, respectUserPreference]);

  // Clear location cache
  const clearLocationCache = useCallback(() => {
    geolocationService.clearCache();
  }, []);

  // Toggle location tracking preference
  const toggleLocationTracking = useCallback((enabled: boolean) => {
    setIsLocationEnabled(enabled);
    localStorage.setItem('location-tracking-enabled', enabled.toString());
    
    if (!enabled) {
      clearLocationCache();
    }
  }, [clearLocationCache]);

  // Check if location permission is granted
  const checkLocationPermission = useCallback(async (): Promise<boolean> => {
    try {
      return await geolocationService.hasLocationPermission();
    } catch (error) {
      console.warn('Failed to check location permission:', error);
      return false;
    }
  }, []);

  return {
    isLocationEnabled,
    isLoading,
    error,
    getLocation,
    getLocationForLoginActivity,
    clearLocationCache,
    toggleLocationTracking,
    checkLocationPermission
  };
}; 